import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || '';
const SESSION_KEY = 'niva_restaurant_session';

const RestaurantAuthContext = createContext(null);

/**
 * Read stored session synchronously from localStorage.
 * Returns parsed session or null.
 */
function readStoredSession() {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.token && parsed?.restaurant) return parsed;
    return null;
  } catch {
    return null;
  }
}

/**
 * Attempt to refresh the Supabase access token directly via the REST API.
 */
async function attemptTokenRefresh(refreshTokenValue) {
  if (!refreshTokenValue) return null;
  try {
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
    const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
    const res = await fetch(`${supabaseUrl}/auth/v1/token?grant_type=refresh_token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': supabaseAnonKey,
      },
      body: JSON.stringify({ refresh_token: refreshTokenValue }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    if (data.access_token && data.refresh_token) {
      return { token: data.access_token, refreshToken: data.refresh_token };
    }
    return null;
  } catch {
    return null;
  }
}

export function RestaurantAuthProvider({ children }) {
  // ─── Initialize state synchronously from localStorage ─────────────────────
  // This means on every page load/refresh, if there's a stored session,
  // isAuthenticated starts as true and loading starts as false immediately —
  // no async wait, no flash to login page.
  const storedSession = readStoredSession();

  const [session, setSession] = useState(storedSession);
  const [restaurant, setRestaurant] = useState(storedSession?.restaurant ?? null);
  const [isAuthenticated, setIsAuthenticated] = useState(!!storedSession);
  const [loading, setLoading] = useState(false); // Never block on load — session is read sync
  const navigate = useNavigate();
  const sessionRef = useRef(session);

  // Keep ref in sync
  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  // Background token refresh on mount if token is near expiry
  useEffect(() => {
    const currentSession = sessionRef.current;
    if (!currentSession?.token || !currentSession?.refreshToken) return;

    // Decode JWT expiry (URL-safe base64)
    let exp = null;
    try {
      const b64 = currentSession.token.split('.')[1]
        .replace(/-/g, '+').replace(/_/g, '/');
      const padded = b64.padEnd(b64.length + (4 - b64.length % 4) % 4, '=');
      const payload = JSON.parse(atob(padded));
      exp = payload.exp ? payload.exp * 1000 : null;
    } catch { /* ignore decode errors */ }

    // If token expires within 5 minutes, refresh it in the background
    if (exp && Date.now() > exp - 5 * 60 * 1000) {
      attemptTokenRefresh(currentSession.refreshToken).then((refreshed) => {
        if (refreshed) {
          const updated = { ...currentSession, token: refreshed.token, refreshToken: refreshed.refreshToken };
          localStorage.setItem(SESSION_KEY, JSON.stringify(updated));
          setSession(updated);
          sessionRef.current = updated;
        }
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Login: call backend, store session, update state.
   */
  const login = useCallback(async (restaurantId, phone, password) => {
    const response = await fetch(`${API_URL}/api/restaurant-portal/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ restaurantId, phone, password }),
    });

    const data = await response.json();

    if (!response.ok) {
      const error = new Error(data.message || data.error || 'Login failed');
      error.status = response.status;
      error.retryAfter = data.retryAfter || null;
      throw error;
    }

    const sessionData = {
      token: data.token,
      refreshToken: data.refreshToken,
      restaurant: data.restaurant,
    };

    localStorage.setItem(SESSION_KEY, JSON.stringify(sessionData));
    sessionRef.current = sessionData;
    setSession(sessionData);
    setRestaurant(data.restaurant);
    setIsAuthenticated(true);

    return data;
  }, []);

  /**
   * Logout: clear storage and state, navigate to login.
   */
  const logout = useCallback(async () => {
    const currentSession = sessionRef.current;

    if (currentSession?.token) {
      try {
        await fetch(`${API_URL}/api/restaurant-portal/logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${currentSession.token}`,
          },
        });
      } catch { /* non-fatal */ }
    }

    localStorage.removeItem(SESSION_KEY);
    sessionRef.current = null;
    setSession(null);
    setRestaurant(null);
    setIsAuthenticated(false);
    navigate('/restaurant-portal/login');
  }, [navigate]);

  /**
   * Authenticated fetch with automatic token refresh on 401.
   * Only clears session and redirects if refresh also fails.
   */
  const authenticatedFetch = useCallback(async (endpoint, options = {}) => {
    const currentSession = sessionRef.current;

    if (!currentSession?.token) {
      // No token at all — redirect to login
      localStorage.removeItem(SESSION_KEY);
      setSession(null);
      setRestaurant(null);
      setIsAuthenticated(false);
      navigate('/restaurant-portal/login');
      throw new Error('No active session');
    }

    const makeRequest = (token) =>
      fetch(`${API_URL}${endpoint}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
          Authorization: `Bearer ${token}`,
        },
      });

    const response = await makeRequest(currentSession.token);

    // On 401 — try refreshing once before giving up
    if (response.status === 401) {
      if (currentSession.refreshToken) {
        const refreshed = await attemptTokenRefresh(currentSession.refreshToken);
        if (refreshed) {
          const newSession = {
            ...currentSession,
            token: refreshed.token,
            refreshToken: refreshed.refreshToken,
          };
          localStorage.setItem(SESSION_KEY, JSON.stringify(newSession));
          sessionRef.current = newSession;
          setSession(newSession);

          const retryResponse = await makeRequest(refreshed.token);
          if (retryResponse.status !== 401) return retryResponse;
        }
      }

      // Refresh also failed — truly expired, clear everything
      localStorage.removeItem(SESSION_KEY);
      sessionRef.current = null;
      setSession(null);
      setRestaurant(null);
      setIsAuthenticated(false);
      navigate('/restaurant-portal/login');
      throw new Error('Session expired');
    }

    return response;
  }, [navigate]);

  return (
    <RestaurantAuthContext.Provider
      value={{ session, restaurant, isAuthenticated, loading, login, logout, authenticatedFetch }}
    >
      {children}
    </RestaurantAuthContext.Provider>
  );
}

export function useRestaurantAuth() {
  const context = useContext(RestaurantAuthContext);
  if (!context) {
    throw new Error('useRestaurantAuth must be used within a RestaurantAuthProvider');
  }
  return context;
}

export default RestaurantAuthContext;
